import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMyDashboards } from '../api/dashboards.ts';
import { useRealEstate, type RealEstateRow, type RealEstateView } from '../api/properties.ts';
import { LoadFailure } from '../components/LoadFailure.tsx';
import { PropertyAddress } from '../components/PropertyAddress.tsx';
import { OccupiedFlag } from '../components/PropertiesPanel.tsx';
import { formatDate } from '../dates.ts';
import { PropertyModal } from '../components/PropertyModal.tsx';
import { PinMap, type PinStyle } from '../components/PinMap.tsx';
import { tokenColour } from '../tokens.ts';

/**
 * The map's look, by kind (14 October). One kind today, a blue dot for an
 * active property; Arthur will want status, tenure or disposition to carry a
 * colour later, which is a new entry here and a key drawn from this table
 * (PinMap's showKey), not a change to the map.
 */
const pinStyles = (): Record<string, PinStyle> => ({
  // The accent, edged in the brand: map pins are the accent (round 3).
  active: { label: 'Active property', fill: tokenColour('accent'), edge: tokenColour('brand'), radius: 7 },
});

/**
 * Real Estate: the working list of property (Arthur, 6 to 8 October).
 *
 * **A property is ACTIVE or INACTIVE, and that is the property's own state**
 * (096). Inactive means no longer the agency's problem, by one of three paths:
 * Sold, Surrendered, or the client discharged by the court. A client's DEATH
 * moves nothing: death is a transition, a discharge an ending, and the house a
 * court ordered the agency to go on selling after its owner died stays on the
 * working list, first, marked.
 *
 * **Every view is reachable whether or not anything is in it** (8 October). The
 * first version kept Sold and Surrendered inside the inactive view, reached by
 * a link below the whole working list: Arthur marked a house Sold and could not
 * find it the next day. So every chip is always shown, with its count, and an
 * empty one says "none". The inactive chips sit in their own labelled group,
 * after the working ones: still not part of the working list, never hidden.
 *
 * **The address is the row's first column and opens the property** (Arthur,
 * 8 October): Address, Property type, Client. The client's name, last, opens
 * the client.
 *
 * Open to whoever may record property work; the server decides.
 */
type Chip =
  | { view: 'active'; key: 'all' | 'Preparing to Surrender' }
  | { view: 'inactive'; key: 'all' | 'Sold' | 'Surrendered' | 'Client discharged' };

const ACTIVE_CHIPS = ['Preparing to Surrender'] as const;
const INACTIVE_CHIPS = ['Sold', 'Surrendered', 'Client discharged'] as const;

function matches(r: RealEstateRow, key: Chip['key']): boolean {
  if (key === 'all') return true;
  if (key === 'Client discharged') return r.client_released;
  // Inactive as Sold or Surrendered, not by the client's discharge.
  if (r.discharged && r.client_released) return false;
  return r.disposition === key;
}

export function RealEstate() {
  const mine = useMyDashboards();
  const [params, setParams] = useSearchParams();
  const view: RealEstateView = params.get('view') === 'inactive' ? 'inactive' : 'active';
  const allowed: readonly string[] = view === 'inactive' ? INACTIVE_CHIPS : ACTIVE_CHIPS;
  const show = params.get('show');
  const chip = { view, key: show && allowed.includes(show) ? show : 'all' } as Chip;
  const asMap = params.get('as') === 'map';
  const choose = (c: Chip, map = asMap) => {
    const next = new URLSearchParams();
    // The map is of active property only; choosing an inactive view shows the list.
    if (c.view === 'inactive') next.set('view', 'inactive');
    if (c.key !== 'all') next.set('show', c.key);
    if (map && c.view === 'active') next.set('as', 'map');
    setParams(next, { replace: true });
  };

  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-[96rem] lg:px-6 lg:py-6">
        <header className="page-head">
          <h1 className="page-title">
            {view === 'inactive' ? 'Real Estate · inactive' : 'Real Estate'}
          </h1>
          <p className="mt-0.5 max-w-[60rem] text-sm text-neutral-500">
            {view === 'inactive'
              ? 'Kept for the record. Sold, surrendered, or the client discharged by the court: the agency has nothing left to do with these.'
              : 'Every property the agency still has work on, whatever the client’s status.'}
          </p>
        </header>
        <div className="page-body">
          {mine.error && !mine.data ? (
            <LoadFailure what="Real Estate" error={mine.error} onRetry={() => void mine.refetch()} retrying={mine.isFetching} />
          ) : mine.isPending ? (
            <p className="px-4 py-10 text-center text-neutral-600">Loading…</p>
          ) : !mine.data?.real_estate ? (
            <div role="alert" className="p-6">
              <h2 className="text-lg font-bold text-neutral-900">Not open to your roles</h2>
              <p className="mt-2 text-neutral-700">Real Estate is open to whoever records property work: tiers 1 and 2.</p>
              <Link
                to="/clients"
                className="mt-5 inline-flex min-h-[48px] items-center rounded-lg bg-neutral-900 px-5 font-semibold text-white"
              >
                Back to clients
              </Link>
            </div>
          ) : (
            <PropertyList chip={chip} choose={choose} asMap={asMap && chip.view === 'active'} />
          )}
        </div>
      </div>
    </div>
  );
}

function PropertyList({ chip, choose, asMap }: { chip: Chip; choose: (c: Chip, map?: boolean) => void; asMap: boolean }) {
  // Both lists, always: every chip shows its count whichever view is open.
  const active = useRealEstate('active');
  const inactive = useRealEstate('inactive');
  const [open, setOpen] = useState<string | null>(null);
  const list = chip.view === 'inactive' ? inactive : active;

  if (list.error && !list.data) {
    return <LoadFailure what="The property list" error={list.error} onRetry={() => void list.refetch()} retrying={list.isFetching} />;
  }
  const count = (rows: RealEstateRow[] | undefined, key: Chip['key']) =>
    rows ? String(rows.filter((r) => matches(r, key)).length) : '…';
  const a = active.data?.properties;
  const i = inactive.data?.properties;
  const on = (c: Chip) => c.view === chip.view && c.key === chip.key;
  const rows = list.data ? list.data.properties.filter((r) => matches(r, chip.key)) : null;
  const deceased = chip.view === 'active' && chip.key === 'all' && a ? a.filter((r) => r.client_deceased).length : 0;

  return (
    <div>
      {/* Views, like the visits page's: the list, or the same rows on a map (active property only). */}
      <div className="flex flex-wrap items-center gap-2 px-4 pt-3 lg:px-5" role="group" aria-label="View">
        <ChipButton on={!asMap} onClick={() => choose(chip, false)}>List</ChipButton>
        <ChipButton on={asMap} onClick={() => choose(chip.view === 'active' ? chip : { view: 'active', key: 'all' }, true)}>On a map</ChipButton>
      </div>
      <div className="flex flex-wrap items-center gap-2 px-4 pt-3 lg:px-5" role="group" aria-label="Choose what to list">
        <ChipButton on={on({ view: 'active', key: 'all' })} onClick={() => choose({ view: 'active', key: 'all' })}>
          All active ({count(a, 'all')})
        </ChipButton>
        {ACTIVE_CHIPS.map((k) => (
          <ChipButton key={k} on={on({ view: 'active', key: k })} onClick={() => choose({ view: 'active', key: k })}>
            {k} ({count(a, k)})
          </ChipButton>
        ))}
        <span className="ml-2 text-xs font-bold tracking-[0.1em] text-neutral-500 uppercase">Inactive, for the record:</span>
        {INACTIVE_CHIPS.map((k) => (
          <ChipButton key={k} quiet on={on({ view: 'inactive', key: k })} onClick={() => choose({ view: 'inactive', key: k })}>
            {k} ({count(i, k)})
          </ChipButton>
        ))}
        <ChipButton quiet on={on({ view: 'inactive', key: 'all' })} onClick={() => choose({ view: 'inactive', key: 'all' })}>
          All inactive ({count(i, 'all')})
        </ChipButton>
      </div>
      {deceased > 0 && (
        <p className="px-4 pt-2 text-sm text-neutral-700 lg:px-5">
          {deceased} {deceased === 1 ? 'property belongs' : 'properties belong'} to a client who has died and
          {deceased === 1 ? ' is' : ' are'} still active. They are listed first: each is either work that continues
          under a court order, or waiting on a decision about who takes it over.
        </p>
      )}

      {asMap && rows ? (
        <PropertyMap rows={rows} fitKey={`${chip.view}:${chip.key}`} onOpen={setOpen} />
      ) : !rows ? (
        <p className="px-4 py-10 text-center text-neutral-600">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="px-4 py-10 text-center text-neutral-600">
          None. {chip.view === 'inactive'
            ? (chip.key === 'all' ? 'No property is inactive.' : `No property is inactive as ${chip.key.toLowerCase()}.`)
            : (chip.key === 'all' ? 'No active property is on file.' : `No active property is ${chip.key.toLowerCase()}.`)}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="mt-2 w-full text-left">
            <thead className="border-b border-neutral-200 text-xs font-bold tracking-[0.1em] text-neutral-500 uppercase">
              <tr>
                <th className="px-4 py-2 lg:px-5">Address</th>
                <th className="px-3 py-2">Property type</th>
                <th className="px-3 py-2">Client</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => <Row key={r.id} r={r} view={chip.view} onOpen={() => setOpen(r.id)} />)}
            </tbody>
          </table>
        </div>
      )}

      {open && <PropertyModal id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function ChipButton({ on, quiet, onClick, children }: { on: boolean; quiet?: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={`min-h-[44px] rounded-full border px-4 text-sm font-semibold ${
        on
          ? 'border-neutral-900 bg-neutral-900 text-white'
          : quiet
            ? 'border-dashed border-neutral-300 text-neutral-600 hover:bg-neutral-50'
            : 'border-neutral-300 text-neutral-800 hover:bg-neutral-50'
      }`}
    >
      {children}
    </button>
  );
}

function Row({ r, view, onOpen }: { r: RealEstateRow; view: RealEstateView; onOpen: () => void }) {
  // A live property whose client has died: visibly unusual, never silently mixed
  // in, and never by colour alone. Somebody picking up the file needs to know why
  // it is still being worked.
  const continues = view === 'active' && r.client_deceased;
  return (
    <tr
      className={`border-b border-neutral-100 align-top ${continues ? 'bg-indigo-50/60' : ''}`}
      data-quick-client={r.client_id}
    >
      <td className={`px-4 py-2 lg:px-5 ${continues ? 'border-l-4 border-indigo-700' : ''}`}>
        {continues && (
          <span className="mb-1 block text-xs font-bold tracking-[0.08em] text-indigo-900 uppercase">
            Client deceased · work continues
          </span>
        )}
        {/* The address opens the property; the client's name, last, opens the client. */}
        <button type="button" onClick={onOpen} aria-haspopup="dialog"
                className="min-h-[44px] w-full rounded-lg px-1 text-left hover:bg-neutral-100">
          <PropertyAddress street={r.street} city={r.city} state={r.state} zip={r.zip}
                           neighbourhood={r.neighbourhood} neighbourhoodSource={r.neighbourhood_source} />
        </button>
        {r.occupied && <OccupiedFlag />}
      </td>
      <td className="px-3 py-2 text-neutral-800">
        {r.property_type_label ?? 'Not recorded'}
        {r.disposition && <span className="block text-sm text-neutral-600">{r.disposition}</span>}
        {r.discharged && (
          <span className="block text-sm text-neutral-600">
            Inactive {r.discharged_on ? `since ${formatDate(r.discharged_on)}` : '(date not known)'}
            {r.client_released ? ': client discharged' : ''}
          </span>
        )}
      </td>
      <td className="px-3 py-2">
        <Link
          to={`/clients/${r.client_id}`}
          state={{ from: 'Real Estate' }}
          className="inline-flex min-h-[44px] items-center text-neutral-900 underline-offset-2 hover:underline"
        >
          {r.display_name}
        </Link>
        <span className="block text-sm text-neutral-600">{r.client_ref} · {r.legal_status}</span>
      </td>
    </tr>
  );
}

/**
 * The same rows as the list, on a map (14 October): the list's own answer from
 * GET /properties, so the map can show nothing the list does not. A pin's
 * tooltip is the address and the client; clicking it opens the property, as the
 * address in the list does. Property not yet placed by the geocoder is counted
 * in words, the way the visits map does.
 */
function PropertyMap({ rows, fitKey, onOpen }: { rows: RealEstateRow[]; fitKey: string; onOpen: (id: string) => void }) {
  const placed = rows.filter((r) => r.latitude !== null && r.longitude !== null);
  const [styles] = useState(pinStyles);
  const missing = rows.length - placed.length;
  return (
    <div className="px-4 pt-3 pb-4 lg:px-5">
      <PinMap
        label="Map of active property"
        styles={styles}
        fitKey={fitKey}
        onOpen={(pin) => onOpen(pin.id)}
        pins={placed.map((r) => ({
          id: r.id,
          latitude: r.latitude,
          longitude: r.longitude,
          kind: 'active',
          title: [r.street, r.city].filter(Boolean).join(', ') || 'Address not recorded',
          detail: `${r.display_name} · ${r.client_ref}`,
        }))}
      />
      <p className="mt-2 text-sm text-neutral-700">
        {placed.length} of {rows.length} {rows.length === 1 ? 'property' : 'properties'} on the map.
        {missing > 0 && ` ${missing} ${missing === 1 ? 'is' : 'are'} not on it: the geocoder has not placed ${missing === 1 ? 'its address' : 'their addresses'} yet. Switch to the list to see ${missing === 1 ? 'it' : 'them'}.`}
      </p>
    </div>
  );
}
